/**
 * SoundPlayer - Plays audio with stretching and manipulation
 *
 * Supports:
 * - Phoneme stretching (e.g., "mmmm..." can be extended)
 * - Pitch shifting
 * - Automatic ducking of background sounds
 */

import { getAudioManager } from './AudioManager';
import { getSoundLoader } from './SoundLoader';

export interface PlayOptions {
  // Which channel to play on
  channel?: 'speech' | 'background';

  // Playback rate (1.0 = normal, 0.5 = half speed, 2.0 = double speed)
  // Use for stretching sounds like "mmmmm..."
  playbackRate?: number;

  // Volume (0.0 to 1.0)
  volume?: number;

  // Pan (-1.0 left, 0.0 center, 1.0 right)
  pan?: number;

  // Auto-duck background when playing speech
  autoDuck?: boolean;

  // Loop the sound (for background music)
  loop?: boolean;

  // Callback when sound finishes
  onEnded?: () => void;
}

export class SoundPlayer {
  private activeSources: Set<AudioBufferSourceNode> = new Set();

  /**
   * Play a sound by URL
   */
  async play(url: string, options: PlayOptions = {}): Promise<void> {
    const audioManager = getAudioManager();
    const soundLoader = getSoundLoader();

    if (!audioManager.isReady()) {
      console.warn('Audio not ready. Call unlock() first.');
      return;
    }

    const context = audioManager.getContext();
    if (!context) return;

    // Load sound if needed
    let buffer = soundLoader.getBuffer(url);
    if (!buffer) {
      try {
        buffer = await soundLoader.load(url);
      } catch (err) {
        console.error(`Failed to load sound ${url}:`, err);
        return;
      }
    }

    // Set defaults
    const {
      channel = 'speech',
      playbackRate = 1.0,
      volume = 1.0,
      pan = 0,
      autoDuck = true,
      loop = false,
      onEnded
    } = options;

    // Duck background if playing speech
    if (channel === 'speech' && autoDuck) {
      audioManager.duckBackground();
    }

    // Create source
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = playbackRate;
    source.loop = loop;

    // Create gain node for volume
    const gainNode = context.createGain();
    gainNode.gain.value = volume;

    // Create panner for stereo positioning
    const pannerNode = context.createStereoPanner();
    pannerNode.pan.value = pan;

    // Connect: source -> gain -> panner -> channel gain
    source.connect(gainNode);
    gainNode.connect(pannerNode);

    const channelGain = channel === 'speech'
      ? audioManager.getSpeechGain()
      : audioManager.getBackgroundGain();

    if (channelGain) {
      pannerNode.connect(channelGain);
    }

    // Track active source
    this.activeSources.add(source);

    // Handle completion
    source.onended = () => {
      this.activeSources.delete(source);

      // Unduck background
      if (channel === 'speech' && autoDuck) {
        audioManager.unduckBackground();
      }

      // Call callback
      if (onEnded) {
        onEnded();
      }
    };

    // Start playback
    source.start(0);
    console.log(`🔊 Playing: ${url} (channel: ${channel}, volume: ${volume}, loop: ${loop}, duration: ${buffer.duration.toFixed(2)}s)`);

    // If looping, return immediately (never completes)
    // Otherwise, wait for audio to finish
    if (loop) {
      return Promise.resolve();
    }

    // Return promise that resolves when audio completes
    return new Promise<void>((resolve) => {
      const originalOnended = source.onended;
      source.onended = (event) => {
        if (originalOnended) originalOnended.call(source, event);
        resolve();
      };
    });
  }

  /**
   * Play a phoneme sound with stretching
   * Useful for continuous sounds like "mmmmm", "sssss", "fffff"
   */
  async playPhoneme(
    url: string,
    duration: number,
    options: Omit<PlayOptions, 'playbackRate'> = {}
  ): Promise<void> {
    const soundLoader = getSoundLoader();

    // Load buffer to check its duration
    let buffer = soundLoader.getBuffer(url);
    if (!buffer) {
      buffer = await soundLoader.load(url);
    }

    // Calculate playback rate to stretch sound to desired duration
    const originalDuration = buffer.duration;
    const playbackRate = originalDuration / duration;

    await this.play(url, {
      ...options,
      playbackRate,
      channel: 'speech'
    });
  }

  /**
   * Stop all currently playing sounds
   */
  stopAll(): void {
    this.activeSources.forEach(source => {
      try {
        source.stop();
      } catch (err) {
        // Source might have already stopped
      }
    });
    this.activeSources.clear();
  }

  /**
   * Get number of currently playing sounds
   */
  getActiveCount(): number {
    return this.activeSources.size;
  }
}

// Singleton instance
let soundPlayer: SoundPlayer | null = null;

export function getSoundPlayer(): SoundPlayer {
  if (!soundPlayer) {
    soundPlayer = new SoundPlayer();
  }
  return soundPlayer;
}
